<script setup>
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  selectedValues: {
    type: [String, Number, Array],
    default: () => [],
  },
  listboxId: { type: String, required: true },
  labelledBy: { type: String, default: '' },
});

const emit = defineEmits(['select', 'search', 'close']);

const { t } = useI18n();

const searchValue = defineModel('searchValue', {
  type: String,
  default: '',
});

const searchInput = ref(null);
const optionRefs = ref([]);

const isSelected = option => {
  if (Array.isArray(props.selectedValues)) {
    return props.selectedValues.includes(option.value);
  }
  return option.value === props.selectedValues;
};

const onInputSearch = event => {
  searchValue.value = event.target.value;
  emit('search', event.target.value);
};

const setOptionRef = (element, index) => {
  if (element) optionRefs.value[index] = element;
};

const focusOption = index => {
  if (!props.options.length) return;
  const normalizedIndex =
    ((index % props.options.length) + props.options.length) %
    props.options.length;
  nextTick(() => optionRefs.value[normalizedIndex]?.focus());
};

const handleOptionKeydown = (event, option, index) => {
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusOption(index + 1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusOption(index - 1);
  } else if (event.key === 'Home') {
    event.preventDefault();
    focusOption(0);
  } else if (event.key === 'End') {
    event.preventDefault();
    focusOption(props.options.length - 1);
  } else if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('select', option);
  } else if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
  }
};

watch(
  () => props.options,
  () => {
    optionRefs.value = [];
  }
);

defineExpose({
  focus: () => searchInput.value?.focus(),
  focusFirstOption: () => focusOption(0),
  focusLastOption: () => focusOption(props.options.length - 1),
});
</script>

<template>
  <div
    v-show="open"
    class="absolute z-50 w-full mt-1 transition-opacity duration-200 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
  >
    <div class="relative border-b border-n-strong">
      <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
      <input
        ref="searchInput"
        :value="searchValue"
        type="search"
        :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
        class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
        @input="onInputSearch"
        @keydown.down.prevent="focusOption(0)"
        @keydown.up.prevent="focusOption(options.length - 1)"
        @keydown.esc.stop.prevent="emit('close')"
      />
    </div>
    <ul
      :id="listboxId"
      class="py-1 mb-0 overflow-auto max-h-60"
      role="listbox"
      :aria-labelledby="labelledBy || undefined"
      :aria-multiselectable="multiple"
    >
      <li
        v-for="(option, index) in options"
        :id="`${listboxId}-option-${index}`"
        :key="`${option.value}-${index}`"
        :ref="element => setOptionRef(element, index)"
        class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2 focus-visible:outline-none"
        :class="{
          'bg-n-alpha-2': isSelected(option),
        }"
        role="option"
        tabindex="-1"
        :aria-selected="isSelected(option)"
        @click="emit('select', option)"
        @keydown="handleOptionKeydown($event, option, index)"
      >
        <span
          :class="{
            'font-medium': isSelected(option),
          }"
          class="text-n-slate-12"
        >
          {{ option.label }}
        </span>
        <span
          v-if="isSelected(option)"
          class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
        />
      </li>
      <li v-if="options.length === 0" class="px-3 py-2 text-sm text-n-slate-11">
        {{ emptyState || t('COMBOBOX.EMPTY_STATE') }}
      </li>
    </ul>
  </div>
</template>
